import { motion } from 'framer-motion';
import { CarouselImages } from '../../../hooks/useCarouselImageRetriever';
import { useState } from 'react';
import { useMobileSwipe } from './hooks/useMobileSwipe';
import { useDesktopSwipe } from './hooks/useDesktopSwipe';
import { SlideImage } from './CarouselSlideImage';
import { SlideImagePlaceholder } from './ImagePlaceholder';

interface CarouselSliderProps {
  images: CarouselImages[];
  currentIndex: number;
  prevSlide: () => void;
  nextSlide: () => void;
}

/**
 * CarouselSlideImages component
 * this component is responsible for rendering the images of the carousel
 * @param currentIndex - current index of the image
 * @param images - images array
 * @param prevSlide - function to go to the previous slide
 * @param nextSlide - function to go to the next slide
 * @returns CarouselSlider component
 */
const CarouselSlideImages = ({ currentIndex, images, prevSlide, nextSlide }: CarouselSliderProps) => {
  const [touchPosition, setTouchPosition] = useState(0);

  const { handleTouchStart, handleTouchMove, handleTouchEnd, isSwiping } = useMobileSwipe({ setTouchPosition, nextSlide, prevSlide });
  const { handleMouseDown, handleMouseMove, handleMouseUp, onMouseLeave, isDragging } = useDesktopSwipe({ setTouchPosition, touchPosition, nextSlide, prevSlide });

  // true if the user is swiping or dragging
  const isSwipingOrDragging = isSwiping || isDragging;

  return (
    <div className='relative min-[350px]:w-full lg:w-[65%] min-[350px]:h-1/2 sm:h-[70%] lg:h-full overflow-hidden'>
      <motion.div
        className='flex relative'
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={onMouseLeave}
        style={{
          width: `${images.length * 100}%`,
          transform: `translateX(calc(-${currentIndex * (100 / images.length)}% + ${touchPosition}px))`,
          transition: isSwipingOrDragging ? 'none' : 'transform 0.3s ease', // Adjust transition property based on dragging state
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        {images.map((image, index) => (
          <div key={index} className='w-full h-full m-auto flex justify-center relative'>
            {image !== undefined && image.url !== '' && <SlideImage image={image} />}
            {(image === undefined || image.url === '') && <SlideImagePlaceholder image={image} />}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default CarouselSlideImages;
